import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useKeptScroll } from '@/lib/keptScroll';
import { useGoBack } from '@/lib/goBack';
import { useChats } from '@/data/store';
import { useAuth } from '@/lib/auth';
import { useStoredChoice } from '@/lib/storedChoice';
import type { ChatMessage, TeamChat } from '@/data/types';
import { Avatar, TeamAvatar } from '@/components/Avatar';
import { BackIcon, CheckIcon } from '@/components/Icons';
import { useOverlay } from '@/components/Overlay';
import { ReportButton } from '@/components/ReportButton';
import { AskButton } from '@/components/AskButton';
import { DutySwitch } from '@/components/DutySwitch';
import { YouButton } from '@/components/YouButton';

// Team: chats between people with Sentinel access, inside CleanMyBed only. Never WhatsApp, so
// free, no 24-hour rule, and customers never see them. Three tabs (Gary, 15 Oct):
// Chats, one-to-one chats; Groups, groups anyone can start and channels head office posts in;
// All, everyone, so anyone can be messaged. Archived one-to-one chats leave Chats but stay
// reachable from All; archived groups move to an Archived heading at the end of Groups
// (mobile review, 19 Oct: they had nowhere to be found again). Every chat opens in the same chat screen as a customer chat (ChatScreen, team mode).

// Coach (Gary, 1 Nov 2026): the groups Ted coaches each area in, kept apart from the human-only groups.
// HQ (Gary, 1 Nov 2026): head office's own channels, the AI team, Leo review, Platform Health and Feedback.
// Gary, 3 Nov 2026: Coach moved to its own bottom tab (CoachListScreen); Team keeps the rest.
const TABS = ['Chats', 'Groups', 'HQ', 'All'] as const;

function lastText(t: TeamChat): string {
  for (let i = t.thread.length - 1; i >= 0; i -= 1) {
    const m = t.thread[i];
    if (m && m.from !== 'day' && m.from !== 'lead') {
      const msg = m as ChatMessage;
      const who = msg.from === 'out' ? 'You: ' : msg.author ? `${msg.author}: ` : '';
      return msg.body.kind === 'text' ? `${who}${msg.body.text}` : `${who}${msg.body.kind === 'photo' ? '📷 Photo' : msg.body.kind === 'voice' ? '🎤 Voice note' : '📄 Document'}`;
    }
  }
  return '';
}

function TeamRow({ t, onOpen, sub }: { t: TeamChat; onOpen: () => void; sub?: string }) {
  return (
    <button type="button" className="chats-row chats-team-row" onClick={onOpen}>
      {t.aiAvatar ? <TeamAvatar who={t.aiAvatar} /> : <Avatar name={t.name} colour={t.colour} />}
      <div className="chats-row-main">
        <div className="chats-row-top">
          <span className="chats-row-name">{t.name}</span>
          <span className={`chats-row-time${t.unread ? ' chats-row-time--hot' : ''}`}>{t.time}</span>
          {/* Compact has no message row, so the count sits beside the time, as on Chats (Gary, 19 Oct: it was missing). */}
          {(t.unread ?? 0) > 0 && <span className="chats-badge chats-badge--sm chats-row-count-compact">{t.unread}</span>}
        </div>
        <div className="chats-row-bottom">
          <span className="chats-row-msg"><span className="chats-row-msg-text">{sub ?? lastText(t)}</span></span>
          {(t.unread ?? 0) > 0 && <span className="chats-badge">{t.unread}</span>}
        </div>
      </div>
    </button>
  );
}

export function TeamListScreen() {
  const listScroll = useKeptScroll<HTMLElement>('team');
  const navigate = useNavigate();
  const { access } = useAuth();
  const { team, teamPeople, openTeam, live } = useChats();
  const [savedTab, setTab] = useStoredChoice<(typeof TABS)[number]>('chats.teamTab', 'Chats', TABS);
  const [opening, setOpening] = useState<string | null>(null);
  const headOffice = access.role === 'head_office';
  const direct = team.filter((t) => (t.kind ?? 'direct') === 'direct' && !t.archived && t.thread.length > 0);
  const channels = team.filter((t) => t.kind === 'channel' && !t.system);
  const hq = team.filter((t) => t.kind === 'channel' && t.system);
  const groups = team.filter((t) => t.kind === 'group' && !t.archived && !t.coaching);
  const archivedGroups = team.filter((t) => t.kind === 'group' && t.archived && !t.coaching);
  const tabs = TABS.filter((t) => t !== 'HQ' || hq.length > 0);
  const tab = savedTab === 'HQ' && !hq.length ? 'Groups' : savedTab;
  const unread = (list: TeamChat[]) => list.filter((t) => (t.unread ?? 0) > 0).length;
  const counts: Record<(typeof TABS)[number], number> = { Chats: unread(direct), Groups: unread([...channels, ...groups]), HQ: unread(hq), All: 0 };

  async function message(personId: string) {
    const existing = team.find((t) => t.personId === personId);
    if (existing) { navigate(`/team/${existing.id}`); return; }
    setOpening(personId);
    const id = await openTeam(personId);
    setOpening(null);
    if (id) navigate(`/team/${id}`);
  }

  return (
    <>
      <header className="chats-listhead">
        <div className="chats-listhead-row">
          <YouButton />
          <h1 className="chats-listhead-title">Team</h1>
          <DutySwitch />
          <ReportButton />
          <AskButton />
        </div>
      </header>
      <div className="chats-chips">
        {tabs.map((t) => (
          <button key={t} type="button" className={`chats-chip${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>
            {/* The green count badge, as on the WhatsApp and Email tabs (Gary, 2 Nov 2026). */}
            {t}{counts[t] > 0 && <span className="chats-badge chats-badge--sm chats-chip-count" aria-label={`${counts[t]} with unread messages`}>{counts[t]}</span>}
          </button>
        ))}
      </div>
      <main ref={listScroll} className="chats-screen">
        {tab === 'Chats' && direct.length === 0 && (
          <p className="chats-empty">No team chats yet. Open All and tap a name to start one.</p>
        )}
        {tab === 'Chats' && direct.map((t) => <TeamRow key={t.id} t={t} onOpen={() => navigate(`/team/${t.id}`)} />)}

        {tab === 'Groups' && (
          <>
            {live && (
              // Small buttons, not full rows (Gary, 1 Nov 2026: they took up the top of the list).
              <div className="chats-team-new">
                <button type="button" className="chats-chip" title="Anyone on the team can start one" onClick={() => navigate('/team/new-group')}>+ New group</button>
                {headOffice && (
                  <button type="button" className="chats-chip" title="Announcements: head office posts, everyone reads" onClick={() => navigate('/team/new-channel')}>+ New channel</button>
                )}
              </div>
            )}
            {channels.length > 0 && <p className="chats-group-title">Channels</p>}
            {channels.map((t) => <TeamRow key={t.id} t={t} onOpen={() => navigate(`/team/${t.id}`)} sub={t.thread.length ? undefined : 'Announcements from head office'} />)}
            {groups.length > 0 && <p className="chats-group-title">Groups</p>}
            {groups.map((t) => <TeamRow key={t.id} t={t} onOpen={() => navigate(`/team/${t.id}`)} sub={t.thread.length ? undefined : `${t.members?.length ?? 0} members`} />)}
            {archivedGroups.length > 0 && <p className="chats-group-title">Archived</p>}
            {archivedGroups.map((t) => <TeamRow key={t.id} t={t} onOpen={() => navigate(`/team/${t.id}`)} sub={t.thread.length ? undefined : `${t.members?.length ?? 0} members`} />)}
            {!channels.length && !groups.length && !archivedGroups.length && <p className="chats-empty">No groups yet.</p>}
          </>
        )}

        {tab === 'HQ' && (
          <>
            <p className="chats-group-title">Head office only</p>
            {hq.map((t) => <TeamRow key={t.id} t={t} onOpen={() => navigate(`/team/${t.id}`)} sub={t.thread.length ? undefined : 'Nothing yet'} />)}
          </>
        )}

        {tab === 'All' && (
          <>
            <p className="chats-group-title">Everyone on the team · customers never see team chats</p>
            {teamPeople.map((p) => {
              const chat = team.find((t) => t.personId === p.id);
              return (
                <button key={p.id} type="button" className="chats-row chats-team-row" disabled={opening === p.id} onClick={() => void message(p.id)}>
                  <Avatar name={p.name} colour={p.name.length} />
                  <div className="chats-row-main">
                    <div className="chats-row-top"><span className="chats-row-name">{p.name}</span></div>
                    <div className="chats-row-bottom">
                      <span className="chats-row-msg"><span className="chats-row-msg-text">{opening === p.id ? 'Opening...' : chat?.archived ? 'Archived chat' : chat && chat.thread.length ? lastText(chat) : 'Tap to message'}</span></span>
                    </div>
                  </div>
                </button>
              );
            })}
          </>
        )}
      </main>
    </>
  );
}

/** Coach (Gary, 3 Nov 2026): Ted's coaching groups, one per area, on their own bottom tab (they were a tab in Team). */
export function CoachListScreen() {
  const listScroll = useKeptScroll<HTMLElement>('coach');
  const navigate = useNavigate();
  const { team } = useChats();
  const coaching = team.filter((t) => t.coaching);
  return (
    <>
      <header className="chats-listhead">
        <div className="chats-listhead-row">
          <YouButton />
          <h1 className="chats-listhead-title">Coach</h1>
          <DutySwitch />
          <ReportButton />
          <AskButton />
        </div>
      </header>
      <main ref={listScroll} className="chats-screen">
        <p className="chats-group-title">Ted coaching each area · sales plan and tasks</p>
        {coaching.map((t) => <TeamRow key={t.id} t={t} onOpen={() => navigate(`/team/${t.id}`)} sub={t.thread.length ? undefined : `${t.members?.length ?? 0} members`} />)}
        {!coaching.length && <p className="chats-empty">You are not in a coaching group yet. Ted coaches each area in its own group.</p>}
      </main>
    </>
  );
}

/** New group (anyone) or new channel (head office): a name, and for a group the people in it. */
export function NewTeamGroupScreen({ channel = false }: { channel?: boolean }) {
  const navigate = useNavigate();
  const goBack = useGoBack();
  const { teamPeople, createGroup, createChannel } = useChats();
  const { toast } = useOverlay();
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  async function create() {
    if (!name.trim() || busy) return;
    setBusy(true);
    const r = channel ? await createChannel(name.trim()) : await createGroup(name.trim(), picked);
    setBusy(false);
    if (r.error || !r.id) { toast(r.error ?? 'That did not work. Try again.'); return; }
    navigate(`/team/${r.id}`, { replace: true });
  }

  return (
    <>
      <header className="chats-topbar chats-topbar--conv">
        <button type="button" className="chats-icon-btn chats-back" aria-label="Back" onClick={() => goBack('/team')}><BackIcon /></button>
        <h1 className="chats-topbar-title">{channel ? 'New channel' : 'New group'}</h1>
      </header>
      <main className="chats-screen chats-screen--grey">
        <div className="chats-pad">
          <input className="chats-input" autoFocus maxLength={60} value={name} onChange={(e) => setName(e.target.value)} placeholder={channel ? 'Channel name, e.g. CleanMyBed news' : 'Group name, e.g. Cape Town ops'} aria-label="Name" />
        </div>
        {channel ? (
          <p className="chats-help-intro">Everyone on the team is in a channel automatically, including people who join later. Head office posts; everyone else reads, reacts and can mute it.</p>
        ) : (
          <>
            <p className="chats-group-title">Tap people to add them ({picked.length} picked)</p>
            <div className="chats-group">
              {teamPeople.map((p) => (
                <button key={p.id} type="button" className="chats-set" aria-pressed={picked.includes(p.id)} onClick={() => toggle(p.id)}>
                  <Avatar name={p.name} colour={p.name.length} small />
                  <div className="chats-set-main">{p.name}</div>
                  <span className={`chats-pick${picked.includes(p.id) ? ' chats-pick--on' : ''}`} aria-hidden="true">{picked.includes(p.id) && <CheckIcon />}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </main>
      {/* Always in view under a long list of people (Gary, 15 Oct). */}
      <div className="chats-sticky-action">
        <button type="button" className="chats-btn" disabled={!name.trim() || busy || (!channel && picked.length === 0)} onClick={() => void create()}>
          {busy ? 'Creating...' : !name.trim() ? (channel ? 'Name the channel first' : 'Name the group first') : channel ? 'Create channel' : `Create group with ${picked.length} ${picked.length === 1 ? 'person' : 'people'}`}
        </button>
      </div>
    </>
  );
}

/** Add people to a group or channel: the same tick list as New group (Gary, 15 Oct). */
export function AddTeamMembersScreen() {
  const { id } = useParams();
  const goBack = useGoBack();
  const { team, teamPeople, addTeamMembers } = useChats();
  const { toast } = useOverlay();
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const c = team.find((t) => t.id === id);
  if (!c) return <Navigate to="/team" replace />;
  const inIt = new Set((c.members ?? []).map((m) => m.id));
  const others = teamPeople.filter((p) => !inIt.has(p.id));
  const toggle = (pid: string) => setPicked((p) => (p.includes(pid) ? p.filter((x) => x !== pid) : [...p, pid]));

  async function add() {
    if (!picked.length || busy || !c) return;
    setBusy(true);
    const err = await addTeamMembers(c.id, picked);
    setBusy(false);
    if (err) { toast(err); return; }
    toast(`${picked.length} ${picked.length === 1 ? 'person' : 'people'} added`);
    goBack(`/team/${c.id}/info`);
  }

  return (
    <>
      <header className="chats-topbar chats-topbar--conv">
        <button type="button" className="chats-icon-btn chats-back" aria-label="Back" onClick={() => goBack(`/team/${c.id}/info`)}><BackIcon /></button>
        <h1 className="chats-topbar-title">Add people to {c.name}</h1>
      </header>
      <main className="chats-screen chats-screen--grey">
        {others.length === 0 ? (
          <p className="chats-empty">Everyone on the team is already in it.</p>
        ) : (
          <>
            <p className="chats-group-title">Tap people to add them ({picked.length} picked)</p>
            <div className="chats-group">
              {others.map((p) => (
                <button key={p.id} type="button" className="chats-set" aria-pressed={picked.includes(p.id)} onClick={() => toggle(p.id)}>
                  <Avatar name={p.name} colour={p.name.length} small />
                  <div className="chats-set-main">{p.name}</div>
                  <span className={`chats-pick${picked.includes(p.id) ? ' chats-pick--on' : ''}`} aria-hidden="true">{picked.includes(p.id) && <CheckIcon />}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </main>
      <div className="chats-sticky-action">
        <button type="button" className="chats-btn" disabled={!picked.length || busy} onClick={() => void add()}>
          {busy ? 'Adding...' : picked.length ? `Add ${picked.length} ${picked.length === 1 ? 'person' : 'people'}` : 'Tick the people to add'}
        </button>
      </div>
    </>
  );
}
